import { useCallback, useState, useEffect, useRef } from 'react';
import { bus } from '../../App';
import { api } from '../../lib/api';
import { TextContentViewer } from '../TextContentViewer';
import { useStatusStore } from '../../state/statusStore';
import { ContextMenu, type ContextMenuItem } from '../ContextMenu';
import { models, getModelKey } from '../../../shared/lib/models';

export function AgentPanel() {
  const [isProcessing, setIsProcessing] = useState(false);
  const [statusData, setStatusData] = useState<string | null>(null);
  const [refreshedState, setRefreshedState] = useState<string | null>(null);
  const [isRunning, setIsRunning] = useState(false);
  const processingRef = useRef(false);
  const websocketConnected = useStatusStore((state) => state.websocketConnected);
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [currentModel, setCurrentModel] = useState<string | null>(null);

  const startProcessing = useCallback(() => {
    if (processingRef.current) {
      return false;
    }
    processingRef.current = true;
    setIsProcessing(true);
    return true;
  }, []);

  const stopProcessing = useCallback(() => {
    processingRef.current = false;
    setIsProcessing(false);
  }, []);

  const handleAction = useCallback(
    async (action: 'pause' | 'resume' | 'cancel' | 'continue') => {
      if (!startProcessing()) {
        return;
      }

      try {
        switch (action) {
          case 'pause':
            await api().agent.pause();
            break;
          case 'resume':
            await api().agent.resume();
            break;
          case 'cancel':
            await api().agent.cancel();
            break;
          case 'continue':
            await api().agent.continue();
            break;
        }
      } catch (error) {
        console.error('[AgentPanel] Failed to execute action:', action, error);
      } finally {
        stopProcessing();
      }
    },
    [startProcessing, stopProcessing],
  );

  const handleClear = useCallback(async () => {
    if (!startProcessing()) {
      return;
    }

    try {
      await api().agent.cancel();
      const id = await api().services.meta.get('active_agent_id')
      await api().services.agent.update(id, {last_message_id: null})
    } catch (error) {
      console.error('[AgentPanel] Failed to clear:', error);
    } finally {
      stopProcessing();
    }
  }, [startProcessing, stopProcessing]);

  const handleRefresh = useCallback(async () => {
    if (!startProcessing()) {
      return;
    }

    try {
      const status = await api().agent.status();
      setStatusData(JSON.stringify(status, null, 2));

      if (status && typeof status === 'object') {
        if ('state' in status && typeof (status as { state?: unknown }).state === 'string') {
          const state = status.state as string;
          setRefreshedState(state);
          setIsRunning(state === 'running');
        }
      }
    } catch (error) {
      console.error('[AgentPanel] Failed to get status:', error);
      setStatusData(JSON.stringify({ error: String(error) }, null, 2));
      setRefreshedState(null);
      setIsRunning(false);
    } finally {
      stopProcessing();
    }
  }, [startProcessing, stopProcessing]);

  const handleReset = useCallback(async () => {
    if (!startProcessing()) {
      return;
    }

    try {
      await api().services.files.reset();
    } catch (error) {
      console.error('[AgentPanel] Failed to reset files:', error);
    } finally {
      stopProcessing();
    }
  }, [startProcessing, stopProcessing]);

  const handleModelsClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    setMenuAnchor(event.currentTarget);
    setMenuOpen((prev) => !prev);
  }, []);

  const handleMenuClose = useCallback(() => {
    setMenuOpen(false);
  }, []);

  const handleModelSelect = useCallback(async (provider: string, model: string) => {
    const modelKey = getModelKey(provider, model)
    await api().services.meta.set('current_model', modelKey);
    setCurrentModel(modelKey);
    console.log('[AgentPanel] Selected provider/model:', provider, model);
  }, []);

  const modelMenuItems: ContextMenuItem[] = models.map((modelInfo) => ({
    label: modelInfo.label,
    value: getModelKey(modelInfo.provider, modelInfo.model),
    onClick: () => handleModelSelect(modelInfo.provider, modelInfo.model),
  }));

  // Load current model on mount
  useEffect(() => {
    const loadCurrentModel = async () => {
      try {
        const model = await api().services.meta.get('current_model');
        if (model && typeof model === 'string') {
          setCurrentModel(model);
        }
      } catch (error) {
        console.error('[AgentPanel] Failed to load current model:', error);
      }
    };
    void loadCurrentModel();
  }, []);

  // Refresh status when panel is mounted
  useEffect(() => {
    void handleRefresh();
  }, [handleRefresh]);

  // Listen to bus events for LLM start/end
  useEffect(() => {
    const unsubscribers: (() => void)[] = [];

    unsubscribers.push(bus.on('llm/start', () => {
      setIsRunning(true);
    }));

    const onError = (message:string) => {
      setIsRunning(false);
      void handleRefresh();
    };

    const stopRunning = () => {
      setIsRunning(false);
    };

    unsubscribers.push(bus.on('llm/end', stopRunning));
    unsubscribers.push(bus.on('llm/error', onError));
    unsubscribers.push(bus.on('llm/cancel', stopRunning));
    unsubscribers.push(bus.on('llm/paused', stopRunning));

    unsubscribers.push(bus.on('meta/set', (event, payload: any) => {
      if (payload?.key === 'active_agent_id') {
        void handleRefresh();
      }
    }));

    return () => {
      unsubscribers.forEach((unsub) => unsub());
    };
  }, [handleRefresh]);

  // Use refreshed state if available for paused state
  const currentState = refreshedState || 'idle';
  const isPaused = currentState === 'paused';
  const isError = currentState === 'error';

  const statusText =
    isRunning ? 'Agent running' : isPaused ? 'Agent paused' : isError ? 'Agent error' : 'Agent idle';

  const badgeClass = `badge badge--large ${
    isRunning
      ? 'badge--positive'
      : isPaused
        ? 'badge--warning'
        : isError
          ? 'badge--critical'
          : 'badge--neutral'
  }`;

  return (
    <div className="panel-shell">
      <div className="panel-controls">
        {isPaused ? (
          <button
            type="button"
            onClick={() => handleAction('resume')}
            className="btn"
            disabled={isProcessing}
          >
            Resume
          </button>
        ) : (
          <button
            type="button"
            onClick={() => handleAction('pause')}
            className="btn"
            disabled={isProcessing}
          >
            Pause
          </button>
        )}
        <button
          type="button"
          onClick={() => handleAction('cancel')}
          className="btn"
          disabled={isProcessing}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => handleAction('continue')}
          className="btn"
          disabled={isProcessing}
        >
          Continue
        </button>
        <button
          type="button"
          onClick={handleClear}
          className="btn"
          disabled={isProcessing}
        >
          Clear
        </button>
        <button
          type="button"
          onClick={handleReset}
          className="btn"
          disabled={isProcessing}
        >
          Reset
        </button>
        <button
          type="button"
          onClick={handleRefresh}
          className="btn"
          disabled={isProcessing}
        >
          Refresh
        </button>
        <div className="agent-models-menu-container">
          <button
            type="button"
            onClick={handleModelsClick}
            className="btn"
            disabled={isProcessing}
          >
            Models
          </button>
          <ContextMenu
            items={modelMenuItems}
            isOpen={menuOpen}
            onClose={handleMenuClose}
            anchorEl={menuAnchor}
            placement="bottom-left"
            checkedValue={currentModel}
          />
        </div>
      </div>
      <div className="panel-content">
        <TextContentViewer
          content={statusData || ''}
          readOnly={true}
        />
      </div>
      <div className="agent-status-bar">
        <span className="agent-status-connection">
          Link {websocketConnected ? 'active' : 'offline'}
        </span>
        <span className={badgeClass}>
          <span className="badge-dot" />
          {statusText}
        </span>
      </div>
    </div>
  );
}
